import React, { useEffect, useRef } from 'react';
import {
  X, Video, Mic, CheckCircle2, Calendar, Square, Camera,
} from 'lucide-react';
import { ANIMAL_ICONS } from '../../theme';
import { ImperialPulseCSS, ImperialProgressBar } from './ImperialProgressBar';

export const BatchStep = ({
  fileQueue, setFileQueue, note, onNoteChange, vibeDate, onDateChange,
  uploading, uploadProgress, uploadPercent, onSubmit, accentColor, category,
  onHunterFinished,
}) => {
  const formatDateInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
    return formatted;
  };

  const icon = ANIMAL_ICONS[category] || ANIMAL_ICONS.legacy;

  /* Trigger roar celebration when the batch reaches 100% on the last file */
  const firedRef = useRef(false);
  const cbRef = useRef(onHunterFinished);
  cbRef.current = onHunterFinished;
  useEffect(() => {
    if (!uploading) { firedRef.current = false; return; }
    const isLast = uploadProgress.current === uploadProgress.total;
    if (isLast && uploadPercent >= 100 && !firedRef.current) {
      firedRef.current = true;
      if (cbRef.current) cbRef.current();
    }
  }, [uploading, uploadProgress.current, uploadProgress.total, uploadPercent]);

  return (
    <div className="flex-1 overflow-y-auto p-5 space-y-4">
      <ImperialPulseCSS />
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 size={18} className="text-[#D4AF37]" />
        <span className="font-['Outfit'] text-sm font-semibold text-white/80">{fileQueue.length} files selected</span>
      </div>

      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
        {fileQueue.map((f, i) => (
          <div key={`${f.name}-${f.size}-${f.lastModified}`} className="relative aspect-square rounded-lg overflow-hidden border border-[#D4AF37]/10" style={{ background: '#0a0a0f' }}>
            {f.type.startsWith('image') ? (
              <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
            ) : f.type.startsWith('video') ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F52BA]/15 to-[#0F52BA]/5">
                <Video size={24} className="text-[#0F52BA]" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5">
                <Mic size={24} className="text-[#D4AF37]" />
              </div>
            )}
            <button onClick={() => setFileQueue((q) => q.filter((_, idx) => idx !== i))}
              className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center">
              <X size={12} className="text-white" />
            </button>
            {uploading && uploadProgress.current > i && (
              <div className="absolute inset-0 bg-[#D4AF37]/20 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-[#D4AF37]" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div>
        <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Vibe Note (shared)</label>
        <textarea value={note} onChange={(e) => onNoteChange(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-[#D4AF37]/15 bg-white/5 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 font-['Outfit'] text-white/90 placeholder-white/20 resize-none"
          placeholder="Share your vibes..." rows="2" data-testid="batch-note-input" />
      </div>

      <div className="flex justify-end">
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/15 bg-white/5 hover:border-[#D4AF37]/30 transition-colors"
          style={{ maxWidth: '170px' }}>
          <Calendar size={13} className="text-[#D4AF37] flex-shrink-0" />
          <input
            type="text" value={vibeDate}
            onChange={(e) => onDateChange(formatDateInput(e.target.value))}
            placeholder="DD/MM/YYYY" maxLength={10}
            className="w-full bg-transparent text-xs font-['Outfit'] text-white/80 placeholder:text-white/20 outline-none"
            style={{ fontStyle: 'italic', letterSpacing: '0.04em' }}
            data-testid="batch-vibe-date-input"
          />
        </div>
      </div>

      {uploading && (
        <ImperialProgressBar
          icon={icon}
          uploadPercent={uploadPercent}
          caption={`${uploadProgress.current} / ${uploadProgress.total} ${uploadPercent < 100 ? '\u2014 the beast hunts...' : '\u2014 captured.'}`}
        />
      )}

      <button onClick={onSubmit} disabled={uploading || fileQueue.length === 0}
        className="w-full text-black py-3 rounded-full font-['Outfit'] font-semibold transition-all disabled:opacity-50 hover:shadow-[0_0_15px_rgba(212,175,55,0.3)]"
        style={{ background: accentColor || '#D4AF37' }}
        data-testid="batch-upload-button">
        {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : `Upload ${fileQueue.length} Vibes`}
      </button>
    </div>
  );
};

export const RecordingStep = ({ mediaType, videoPreviewRef, isRecording, recordingTime, formatTime, onStop, accentColor }) => (
  <div className="flex-1 flex flex-col">
    {mediaType === 'video' && (
      <div className="relative flex-1 min-h-[300px] bg-black">
        <video ref={videoPreviewRef} autoPlay playsInline muted className="w-full h-full object-cover" />
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-['Outfit']">
          <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse" />
          REC {formatTime(recordingTime)}
        </div>
      </div>
    )}
    {mediaType === 'audio' && (
      <div className="flex-1 flex flex-col items-center justify-center py-12 gap-4">
        <div className="relative">
          <div className="w-28 h-28 rounded-full flex items-center justify-center animate-pulse" style={{ background: `${accentColor || '#D4AF37'}20` }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: accentColor || '#D4AF37' }}>
              <Mic size={36} className="text-black" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-red-500 font-['Outfit'] font-semibold">
          <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
          Recording {formatTime(recordingTime)}
        </div>
      </div>
    )}
    <div className="flex justify-center py-4" style={{ background: '#05050A' }}>
      <button onClick={onStop}
        className="w-16 h-16 rounded-full bg-red-600 shadow-lg flex items-center justify-center hover:bg-red-700 transition-colors"
        data-testid="stop-recording-button">
        <Square size={22} className="text-white" fill="white" />
      </button>
    </div>
  </div>
);

export const CameraStep = ({ videoPreviewRef, onTakePhoto, accentColor }) => (
  <div className="flex-1 flex flex-col">
    <div className="relative flex-1 min-h-[300px] bg-black">
      <video ref={videoPreviewRef} autoPlay playsInline muted className="w-full h-full object-cover" />
    </div>
    <div className="flex justify-center py-4" style={{ background: '#05050A' }}>
      <button onClick={onTakePhoto}
        className="w-16 h-16 rounded-full border-4 border-[#D4AF37]/30 shadow-lg flex items-center justify-center"
        style={{ background: accentColor || '#D4AF37', boxShadow: '0 0 20px rgba(212,175,55,0.3)' }}
        data-testid="shutter-button">
        <Camera size={24} className="text-black" />
      </button>
    </div>
  </div>
);
